
import React, { useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import {
  LogIn,
  Key,
  Mail,
  ShieldAlert,
  Eye,
  EyeOff,
  X,
} from "lucide-react";
import api from "../../services/api_client";

const LoginForm = ({ onLoginSuccess }) => {
  const { login } = useAuth();

  const [selectedRole, setSelectedRole] = useState("employee");
  const [email, setEmail] = useState("employee@smarthr.com");
  const [password, setPassword] = useState("admin");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Afficher ou masquer le mot de passe
  const [showPassword, setShowPassword] = useState(false);

  // Mot de passe oublié
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotError, setForgotError] = useState("");
  const [forgotSuccess, setForgotSuccess] = useState("");

  const handleRoleSelect = (role) => {
    setSelectedRole(role);
    setError("");

    if (role === "employee") {
      setEmail("employee@smarthr.com");
    } else if (role === "manager") {
      setEmail("manager@smarthr.com");
    } else if (role === "rh") {
      setEmail("hr@smarthr.com");
    }

    setPassword("admin");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const result = await login(email, password);

      if (result.success) {
        if (onLoginSuccess) {
          onLoginSuccess(result.role);
        }
      } else {
        setError(result.error || "Identifiants incorrects.");
      }
    } catch (err) {
      setError(
        err.response?.data?.detail ||
          "Une erreur est survenue pendant la connexion."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    setForgotError("");
    setForgotSuccess("");
    setForgotLoading(true);

    try {
      const response = await api.post("/auth/forgot-password", {
        email: forgotEmail,
      });

      setForgotSuccess(
        response.data?.message ||
          "Si cette adresse existe, un lien de réinitialisation sera envoyé."
      );
    } catch (err) {
      setForgotError(
        err.response?.data?.detail ||
          "Impossible d'envoyer le lien. Veuillez réessayer."
      );
    } finally {
      setForgotLoading(false);
    }
  };

  const openForgotModal = () => {
    setForgotEmail(email);
    setForgotError("");
    setForgotSuccess("");
    setShowForgotModal(true);
  };

  const closeForgotModal = () => {
    if (forgotLoading) return;

    setShowForgotModal(false);
    setForgotError("");
    setForgotSuccess("");
  };

  return (
    <div className="login-card-container">
       <div className="login-logo-section">
          <img
            src="/LogoCSI.png"
            alt="Logo CSI"
            className="login-logo"
          />

        </div>
      <div className="login-card-header">
        <h3 className="login-title">Connexion Smart-HR</h3>
        <p className="login-subtitle">
          Sélectionnez un rôle de test ou saisissez vos identifiants
        </p>
      </div>

      <div className="role-selector-grid">
        <button
          type="button"
          onClick={() => handleRoleSelect("employee")}
          className={`role-tile ${
            selectedRole === "employee" ? "active" : ""
          }`}
        >
          <div className="role-tile-icon tile-employee">👥</div>
          <span className="role-tile-label">Employé</span>
        </button>

        <button
          type="button"
          onClick={() => handleRoleSelect("manager")}
          className={`role-tile ${
            selectedRole === "manager" ? "active" : ""
          }`}
        >
          <div className="role-tile-icon tile-manager">💼</div>
          <span className="role-tile-label">Manager</span>
        </button>

        <button
          type="button"
          onClick={() => handleRoleSelect("rh")}
          className={`role-tile ${
            selectedRole === "rh" ? "active" : ""
          }`}
        >
          <div className="role-tile-icon tile-rh">🛡️</div>
          <span className="role-tile-label">RH / Admin</span>
        </button>
      </div>

      {error && (
        <div className="login-error-box">
          <ShieldAlert size={18} />
          <span>{error}</span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="login-form">
        <div className="form-group">
          <label className="form-label" htmlFor="login-email">
            Adresse email
          </label>

          <div className="input-with-icon">
            <Mail size={16} className="input-icon" />

            <input
              id="login-email"
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setSelectedRole("");
              }}
              required
              placeholder="votre.email@smarthr.com"
              className="form-input"
              disabled={loading}
            />
          </div>
        </div>

        <div className="form-group">
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: "8px",
              marginBottom: "8px",
            }}
          >
            <label className="form-label" htmlFor="login-password">
              Mot de passe
            </label>

            
          </div>

          <div className="input-with-icon">
            <Key size={16} className="input-icon" />

            <input
              id="login-password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setSelectedRole("");
              }}
              required
              placeholder="Saisissez votre mot de passe"
              className="form-input"
              disabled={loading}
            />

            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={
                showPassword
                  ? "Masquer le mot de passe"
                  : "Afficher le mot de passe"
              }
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                color: "#64748b",
                display: "flex",
                alignItems: "center",
                padding: "4px",
                marginLeft: "-38px",
                zIndex: 1,
              }}
            >
              {showPassword ? (
                <EyeOff size={17} />
              ) : (
                <Eye size={17} />
              )}
            </button>
          </div>

          <button
              type="button"
              onClick={openForgotModal}
              style={{
                background: "none",
                border: "none",
                padding: 0,
                color: "#1555bd",
                fontSize: "12px",
                cursor: "pointer",
                fontFamily: "inherit",
              }}
            >
              Mot de passe oublié ?
            </button>
        </div>

        <button
          type="submit"
          disabled={loading}
          className={`btn-login-submit ${
            loading ? "loading" : ""
          }`}
        >
          {loading ? (
            <span className="spinner"></span>
          ) : (
            <>
              <LogIn size={18} />
              <span>Se connecter</span>
            </>
          )}
        </button>
      </form>

      {/* Fenêtre de réinitialisation */}
      {showForgotModal && (
        <div
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) {
              closeForgotModal();
            }
          }}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 1000,
            background: "rgba(15, 23, 42, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px",
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="forgot-password-title"
            style={{
              background: "#fff",
              width: "100%",
              maxWidth: "420px",
              padding: "28px",
              borderRadius: "16px",
              boxShadow: "0 20px 50px rgba(0,0,0,0.18)",
              boxSizing: "border-box",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: "12px",
                marginBottom: "16px",
              }}
            >
              <h3
                id="forgot-password-title"
                style={{
                  margin: 0,
                  fontSize: "19px",
                  color: "#0f172a",
                }}
              >
                Mot de passe oublié ?
              </h3>

              <button
                type="button"
                onClick={closeForgotModal}
                disabled={forgotLoading}
                aria-label="Fermer"
                style={{
                  border: "none",
                  background: "none",
                  cursor: "pointer",
                  color: "#64748b",
                }}
              >
                <X size={20} />
              </button>
            </div>

            <p
              style={{
                color: "#64748b",
                fontSize: "14px",
                lineHeight: 1.6,
                marginBottom: "20px",
              }}
            >
              Saisissez votre adresse email pour recevoir un lien
              de réinitialisation de votre mot de passe.
            </p>

            {forgotError && (
              <div
                role="alert"
                style={{
                  padding: "12px",
                  marginBottom: "16px",
                  borderRadius: "8px",
                  background: "#fef2f2",
                  color: "#b91c1c",
                  fontSize: "13px",
                }}
              >
                {forgotError}
              </div>
            )}

            {forgotSuccess ? (
              <>
                <div
                  role="status"
                  style={{
                    padding: "12px",
                    borderRadius: "8px",
                    background: "#f0fdf4",
                    color: "#166534",
                    fontSize: "13px",
                    lineHeight: 1.6,
                  }}
                >
                  {forgotSuccess}
                </div>

                <button
                  type="button"
                  onClick={closeForgotModal}
                  style={{
                    width: "100%",
                    marginTop: "20px",
                    padding: "12px",
                    border: "none",
                    borderRadius: "8px",
                    background: "#1555bd",
                    color: "#fff",
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  Fermer
                </button>
              </>
            ) : (
              <form onSubmit={handleForgotSubmit}>
                <div className="form-group">
                  <label
                    className="form-label"
                    htmlFor="forgot-email"
                  >
                    Adresse email
                  </label>

                  <div className="input-with-icon">
                    <Mail size={16} className="input-icon" />

                    <input
                      id="forgot-email"
                      type="email"
                      value={forgotEmail}
                      onChange={(e) =>
                        setForgotEmail(e.target.value)
                      }
                      required
                      placeholder="votre.email@smarthr.com"
                      className="form-input"
                      disabled={forgotLoading}
                    />
                  </div>
                </div>

                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: "10px",
                    marginTop: "20px",
                  }}
                >
                  <button
                    type="button"
                    onClick={closeForgotModal}
                    disabled={forgotLoading}
                    style={{
                      padding: "11px 15px",
                      border: "1px solid #dbe3ef",
                      borderRadius: "8px",
                      background: "#fff",
                      color: "#334155",
                      cursor: "pointer",
                    }}
                  >
                    Annuler
                  </button>

                  <button
                    type="submit"
                    disabled={forgotLoading}
                    style={{
                      padding: "11px 15px",
                      border: "none",
                      borderRadius: "8px",
                      background: "#1555bd",
                      color: "#fff",
                      fontWeight: 600,
                      cursor: forgotLoading ? "wait" : "pointer",
                    }}
                  >
                    {forgotLoading
                      ? "Envoi en cours..."
                      : "Envoyer le lien"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default LoginForm;
